import { DocumentSource } from '@monetize/types/app';
import { FC } from 'react';
import { MdClose, MdOutlineError, MdRefresh } from 'react-icons/md';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MIcon,
  MSpinner,
  MTag,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { QuoteDocumentUpload } from '../../../../../store/quoteDocumentUpload.store';
import { getRowSourceLabel } from '../quoteDocumentsListUtils';
import {
  QuoteDocumentsMode,
  ROW_GRID_TEMPLATE_COLUMNS,
  SETTINGS_ROW_GRID_TEMPLATE_COLUMNS,
} from './constants';
import { DocumentFormatTag } from './DocumentFormatTag';

interface PendingDocumentRowProps {
  pending: QuoteDocumentUpload;
  mode: QuoteDocumentsMode;
  quoteDocumentTypes: Array<{ id: string; label: string; value: string }>;
  onSelectType: (documentTypeId: string) => void;
  onRetry: () => void;
  onRemove: () => void;
}

export const PendingDocumentRow: FC<PendingDocumentRowProps> = ({
  pending,
  mode,
  quoteDocumentTypes,
  onSelectType,
  onRetry,
  onRemove,
}) => {
  const isSelectingType = pending.status === 'selecting-type';
  const isError = pending.status === 'error';
  const documentTypeLabel = quoteDocumentTypes.find(
    (type) => type.id === pending.documentTypeId,
  )?.label;

  return (
    <MBox
      bg="white"
      borderRadius="md"
      borderTopWidth="1px"
      borderRightWidth="1px"
      borderBottomWidth="1px"
      borderStyle={isSelectingType ? 'dashed' : 'solid'}
      borderColor={isSelectingType ? 'tGray.lightGray' : 'border.subtle'}
      borderLeftWidth="4px"
      borderLeftStyle="solid"
      borderLeftColor={isError ? 'tRed.base' : 'tOrange.base'}
      p={0}
      mb={2}
    >
      <MBox
        display="grid"
        gridTemplateColumns={
          mode === QuoteDocumentsMode.SETTINGS
            ? SETTINGS_ROW_GRID_TEMPLATE_COLUMNS
            : ROW_GRID_TEMPLATE_COLUMNS
        }
        gap={3}
        alignItems="center"
        pl="calc(0.75rem - 3px)"
        pr={3}
        py={2}
        minH="56px"
      >
        <MBox />
        <MFlex alignItems="center" gap={2} minW={0}>
          <DocumentFormatTag mediaType={pending.file.type} />
          <MText fontSize="sm" fontStyle="italic" truncate minW={0}>
            {pending.file.name}
          </MText>
        </MFlex>
        <MFlex alignItems="center" gap={2} minW={0}>
          {isSelectingType ? (
            <>
              <MBox minW={0} w="100%">
                <MCustomSelect
                  name={`pending-document-type-${pending.localId}`}
                  inputTestId={`quote-document-type-select-pending-${pending.localId}`}
                  items={quoteDocumentTypes}
                  itemTitle="label"
                  itemValue="id"
                  placeholder="Select document type"
                  value=""
                  onChange={(documentTypeId: string) =>
                    onSelectType(documentTypeId)
                  }
                />
              </MBox>
              <MTooltip
                label="This file hasn't been saved yet. Select a document type to add it to the quote."
                placement="top-start"
              >
                <MBox as="span" display="inline-flex">
                  <MIcon
                    as={MdOutlineError}
                    boxSize={4}
                    color="tOrange.tangerine"
                  />
                </MBox>
              </MTooltip>
            </>
          ) : (
            <MBox minW={0} maxW="100%">
              <MText
                maxW="100%"
                h="auto"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {documentTypeLabel}
              </MText>
            </MBox>
          )}
        </MFlex>
        <MBox minW={0}>
          {mode === QuoteDocumentsMode.QUOTE && (
            <MTag size="sm">{getRowSourceLabel(DocumentSource.QUOTE)}</MTag>
          )}
        </MBox>
        <MFlex justifySelf="end" gap={1}>
          {isSelectingType && (
            <MCustomIconButton
              icon={MdClose}
              aria-label={`Remove pending upload ${pending.file.name}`}
              data-testid={`quote-document-pending-remove-btn-${pending.localId}`}
              btnSize={5}
              containerSize={6}
              onClick={onRemove}
            />
          )}
          {pending.status === 'uploading' && <MSpinner size="sm" />}
          {isError && (
            <>
              <MTooltip label="Retry" placement="top">
                <MBox as="span" display="inline-flex">
                  <MCustomIconButton
                    icon={MdRefresh}
                    aria-label={`Retry uploading ${pending.file.name}`}
                    data-testid={`quote-document-pending-retry-btn-${pending.localId}`}
                    btnSize={5}
                    containerSize={6}
                    onClick={onRetry}
                  />
                </MBox>
              </MTooltip>
              <MTooltip label="Remove" placement="top">
                <MBox as="span" display="inline-flex">
                  <MCustomIconButton
                    icon={MdClose}
                    aria-label={`Remove failed upload ${pending.file.name}`}
                    data-testid={`quote-document-pending-remove-btn-${pending.localId}`}
                    btnSize={5}
                    containerSize={6}
                    onClick={onRemove}
                  />
                </MBox>
              </MTooltip>
            </>
          )}
        </MFlex>
      </MBox>

      {isError && (
        <MFlex
          alignItems="center"
          gap={1}
          px={3}
          pb={2}
          pl="calc(0.75rem - 3px)"
        >
          <MIcon
            as={MdOutlineError}
            boxSize={4}
            color="tRed.base"
            flexShrink={0}
          />
          <MText fontSize="xs" color="tRed.base">
            {pending.errorMessage}
          </MText>
        </MFlex>
      )}
    </MBox>
  );
};
